import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import StatusBadge from '../../../components/StatusBadge';
import Button from '../../../components/Button';
import RowActions from '../../../components/RowActions';
import TenantHealthCard from '../../../components/TenantHealthCard';
import { Modules, RouteParamsTypes } from '../../../enums';
import { useNavigate } from 'react-router-dom';

interface TenantRow {
  id: number;
  tenant_code: string;
  tenant_name: string;
  slug: string;
  product_code: string | null;
  plan_name: string | null;
  status_name: string | null;
  status_code: string | null;
  status_colour: string | null;
  isolation_mode: 'shared' | 'dedicated';
  owner_email: string;
  country_name: string | null;
  subscription_start_date: string;
}

interface TenantRowProps {
  item: TenantRow;
  className?: string;
  canEdit: boolean;
  canDelete: boolean;
  selectMode?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: number) => void;
}

/**
 * US-050 AC1: desktop gets a lazy-loaded hover tooltip (health data fetched once,
 * on first hover, via TenantHealthCard's own on-mount fetch); mobile (< md) gets
 * an expandable sub-row instead, since hover has no equivalent on touch.
 */
const TenantRowContent: React.FC<TenantRowProps> = ({
  item,
  className,
  canEdit,
  canDelete,
  selectMode,
  selected,
  onToggleSelect,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleRowClick = useCallback(() => navigate(`${Modules.tenants}/${item.id}`), [navigate, item.id]);

  const nameCell = (
    <td className="cursor-pointer" onClick={handleRowClick} onMouseEnter={() => setHovered(true)}>
      <div className="d-flex align-items-center gap-2">
        {!isDesktop && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
        )}
        {item.tenant_name}
      </div>
    </td>
  );

  return (
    <>
      <tr className={className}>
        {selectMode && (
          <td onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              className="form-check-input"
              checked={!!selected}
              onChange={() => onToggleSelect?.(item.id)}
            />
          </td>
        )}
        <td className="cursor-pointer" onClick={handleRowClick}>
          {item.tenant_code}
        </td>
        {isDesktop ? (
          <Tooltip
            placement="right"
            title={hovered ? <TenantHealthCard tenantId={item.id} compact /> : ''}
            componentsProps={{ tooltip: { sx: { maxWidth: 320, p: 2 } } }}
          >
            {nameCell}
          </Tooltip>
        ) : (
          nameCell
        )}
        <td>{item.product_code ?? '—'}</td>
        <td>{item.plan_name ?? '—'}</td>
        <td>
          {item.status_name && item.status_colour ? (
            <StatusBadge label={item.status_name} colour={item.status_colour} />
          ) : (
            '—'
          )}
        </td>
        <td>{item.owner_email}</td>
        <td>{item.country_name ?? '—'}</td>
        <td>{item.subscription_start_date}</td>
        <td className="text-end text-nowrap">
          <RowActions
            actions={[
              {
                id: 'view',
                label: t('tenants.list.pageTexts.table.actions.view') || 'View',
                icon: <i className="ri-eye-line" />,
                onClick: handleRowClick,
              },
              canEdit && {
                id: 'edit',
                label: t('tenants.list.pageTexts.table.actions.edit') || 'Edit',
                icon: <i className="ri-pencil-line" />,
                onClick: () => navigate(`${Modules.tenants}${RouteParamsTypes.edit}/${item.id}`),
              },
              canDelete && {
                id: 'delete',
                label: t('tenants.list.pageTexts.table.actions.delete') || 'Delete',
                icon: <i className="ri-delete-bin-line" />,
                danger: true,
                onClick: handleRowClick,
              },
            ]}
          />
        </td>
      </tr>
      {!isDesktop && (
        <tr>
          <td colSpan={selectMode ? 10 : 9} className="p-0 border-0">
            <Collapse in={expanded}>
              <div className="px-4 py-3 bg-light">
                <TenantHealthCard tenantId={item.id} compact />
              </div>
            </Collapse>
          </td>
        </tr>
      )}
    </>
  );
};

export default TenantRowContent;
